/**
 * Bet-size preset computation for the action bar (docs/UI_REFERENCE.md
 * §4). Pure number-crunching, no game-state dependency beyond the plain
 * numbers callers pull out of a `HandState`/`BotView` -- kept this way so
 * it's trivially unit-testable and reusable from both the store and any
 * future component tests.
 */

export interface BetPreset {
  /** Human-readable label, e.g. "2.5bb", "3x", "75%". */
  readonly label: string;
  /** Desired total street-bet ("to" amount), in chips, BEFORE clamping to the legal [min,max] range. */
  readonly toChips: number;
}

/** Clamps a desired "to" amount into a legal [min, max] bet/raise range, rounding to the nearest integer chip. */
export function clampToLegalRange(min: number, max: number, desired: number): number {
  return Math.max(min, Math.min(max, Math.round(desired)));
}

/** Preflop opening presets (no bet yet to call this street): fixed bb multiples. */
export function preflopOpenPresets(bbChips: number): BetPreset[] {
  return [2, 2.5, 3, 4].map((mult) => ({
    label: `${mult}bb`,
    toChips: Math.round(mult * bbChips),
  }));
}

/**
 * "vs bet" presets: multiples of the opponent's current street bet, plus
 * an explicit Max (all-in) entry. Used both preflop (facing a raise) and
 * postflop (facing a bet/raise).
 */
export function vsBetPresets(oppBet: number, allInTo: number): BetPreset[] {
  const presets: BetPreset[] = [2.5, 3, 4].map((mult) => ({
    label: `${mult}x`,
    toChips: Math.round(oppBet * mult),
  }));
  presets.push({ label: "Max", toChips: allInTo });
  return presets;
}

/** Postflop opening presets (no bet yet to call this street): pot fractions. */
export function postflopPotPresets(pot: number): BetPreset[] {
  return [0.33, 0.5, 0.75, 1.0].map((frac) => ({
    label: `${Math.round(frac * 100)}%`,
    toChips: Math.round(pot * frac),
  }));
}

export interface PresetContext {
  readonly street: "preflop" | "flop" | "turn" | "river";
  /** True if the player to act is facing a live bet (a "call" action is legal). */
  readonly facingBet: boolean;
  readonly oppBet: number;
  readonly pot: number;
  readonly bbChips: number;
  readonly allInTo: number;
}

/** Picks the appropriate preset set (docs/UI_REFERENCE.md §4) for the current situation, already clamped into [min, max]. */
export function presetsFor(ctx: PresetContext, min: number, max: number): BetPreset[] {
  const raw = ctx.facingBet
    ? vsBetPresets(ctx.oppBet, ctx.allInTo)
    : ctx.street === "preflop"
      ? preflopOpenPresets(ctx.bbChips)
      : postflopPotPresets(ctx.pot);

  // Dedupe by clamped value (e.g. a 4x raise and Max can coincide when short-stacked).
  const seen = new Set<number>();
  const clamped: BetPreset[] = [];
  for (const p of raw) {
    const toChips = clampToLegalRange(min, max, p.toChips);
    if (seen.has(toChips)) continue;
    seen.add(toChips);
    clamped.push({ label: p.label, toChips });
  }

  // All-in is always offered as the rightmost preset, in every street and
  // situation (docs/UI_REFERENCE.md feedback #4). If a clamped preset
  // already lands on the all-in amount (e.g. this preset set's own "Max",
  // or any preset squeezed to the same value by a short stack), relabel
  // that entry "All-in" and move it to the end instead of duplicating it.
  if (ctx.allInTo > 0) {
    const allInChips = clampToLegalRange(min, max, ctx.allInTo);
    const dupIdx = clamped.findIndex((p) => p.toChips === allInChips);
    if (dupIdx >= 0) clamped.splice(dupIdx, 1);
    clamped.push({ label: "All-in", toChips: allInChips });
  }

  return clamped;
}
